// eslint-disable-next-line import/extensions
import { CountryCode, CurrencyCode } from "consts";

import { PracticeSlug } from "models";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PRACTICE_LEVEL = {
  field: "field",
  farm: "farm",
} as const;

export const ACCOUNTING_METHODOLOGY = {
  inventory: "inventory",
  intervention: "intervention",
} as const;

export const INCENTIVE_NAME = {
  biodiversity: "biodiversity",
  companionCrops: "companion_crops",
  conservationTillage: "conservation_tillage",
  catchCrop: "catch_crops",
  fourR: "four_r",
} as const;

export type PracticeLevel = (typeof PRACTICE_LEVEL)[keyof typeof PRACTICE_LEVEL];
export type AccountingMethodology = (typeof ACCOUNTING_METHODOLOGY)[keyof typeof ACCOUNTING_METHODOLOGY];
export type SocModel = "RegionalValues" | "FieldSocModel";
export type BaselineMethodology = "averaged";
export type CreditOwner = "klim" | "farmer";
export type PaymentBasis = "ha" | "kg";
export type RequirementLevel = "requiredForSaving" | "requiredForProgram" | "optional" | "optionalAlwaysShown";
export type IncentiveName = (typeof INCENTIVE_NAME)[keyof typeof INCENTIVE_NAME];

export interface Incentive {
  readonly amount: number;
  readonly name: IncentiveName;
  readonly paymentBasis: PaymentBasis;
  readonly elementIds?: string[];
  readonly requirementLevel?: RequirementLevel;
}

export interface Practice {
  readonly slug: PracticeSlug;
  readonly required: boolean;
  readonly dependencies: PracticeSlug[];
  readonly incentives: Incentive[];
  readonly baselineYears: number;
  readonly baselineMethodology: BaselineMethodology | null;
  readonly enrollable: boolean;
}

export interface PayoutDetails {
  readonly pointPrice: number;
}

export interface PayoutDetailsPerCategory {
  readonly removals: PayoutDetails;
  readonly avoidances: PayoutDetails;
  readonly reductions: PayoutDetails;
}

export interface YearlyFormPayout {
  readonly [payoutPerYear: string]: number;
}

export interface FarmLevelFootprinting {
  formSubmissionBonuses: {
    // Per-crop means the bonus is applied to each required crop form
    // All-crops means the bonus is applied when *all* crop forms are finished
    cropPayouts: "per-crop" | "all-crops";
    crop?: YearlyFormPayout;
    farm?: YearlyFormPayout;
  };
  programCrops: string[]; // CropIds from practices_crops
  startYear: number;
  disabledYears?: number[];
  verificationStartYear?: number; // Dictates in which year verification for the footprinting data should start
}

export interface PlatformFeatures {
  readonly fieldManagement?: {
    readonly removalsExperience?: boolean;
    readonly payouts?: boolean;
    readonly outcomeBasedIncentives?: boolean; // only relevant for inventory
    readonly impactPanel?: boolean;
    readonly submission?: boolean;
  };
  readonly footprinting?: {
    readonly enabled?: boolean;
    readonly results?: boolean;
    readonly dairy?: boolean;
    readonly payouts?: boolean;
  };
  readonly planning?: {
    readonly simulation?: boolean;
    readonly cropMixComposer?: boolean;
  };
}

export interface CarbonProgram {
  readonly id: string;
  readonly programName: string;
  readonly programSlug: string;
  readonly bannerImageUrl: string;
  readonly accountingMethodology: AccountingMethodology;
  readonly partnerId: string | null;
  readonly partnerName?: string | null;
  readonly country: CountryCode;
  readonly currency: CurrencyCode;
  readonly credits: {
    readonly ownership: CreditOwner;
  };
  readonly payoutLimitsHa?: number;
  readonly socModel: SocModel;
  readonly hidePayouts?: boolean;
  readonly isEnabled: boolean;
  readonly practices: Practice[];
  readonly payouts: PayoutDetailsPerCategory;
  readonly farmLevelFootprinting?: FarmLevelFootprinting;
  readonly regulations: {
    readonly field: {
      readonly redzoneCoverCrops?: boolean;
      readonly redzoneFertilizers?: boolean;
      readonly leakageFertilizers?: boolean;
    };
  };
  readonly reportingPeriod?: {
    readonly start: string;
    readonly end: string;
  };
  readonly farmCount?: number;
  readonly platformFeatures: PlatformFeatures;
  readonly startYear?: number;
}
